import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AdminAvatar } from "@/components/admin/avatar";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon, type IconName } from "@/components/admin/icon";
import { Kpi } from "@/components/admin/kpi";
import { Tag } from "@/components/admin/tag";
import { useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { usePermissions } from "@/hooks/use-permissions";
import {
	useAddNote,
	useAdjustLoyalty,
	useCustomer,
} from "@/lib/customers/customers-hooks";
import { customersApi } from "@/lib/customers/customers-api";
import { useOrdersList } from "@/lib/orders/orders-hooks";
import {
	ORDER_STATUS_SUB_LABEL,
	PAYMENT_METHOD_LABEL,
	PAYMENT_STATUS_LABEL,
} from "@/lib/orders/labels";
import { fmtFCFA } from "@/lib/format";
import { statusOrderV2 } from "@/lib/status";
import type { TagClass } from "@/lib/status";

const TIER_VARIANT: Record<string, TagClass> = {
	platinum: "terra",
	gold: "ochre",
	silver: "info",
	bronze: "neutral",
};

const CHANNEL_ICON: Record<string, IconName> = {
	email: "mail",
	sms: "chat",
	phone: "phone",
	whatsapp: "chat",
};

export function AdminCustomerDetailPage() {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const toast = useToast();
	const perms = usePermissions();

	// Navigation par onglets
	const [activeTab, setActiveTab] = useState<"overview" | "orders" | "loyalty" | "security">("overview");

	// PII Access state
	const [piiReason, setPiiReason] = useState<string>("");
	const [piiReasonInput, setPiiReasonInput] = useState<string>("");
	const [showPiiModal, setShowPiiModal] = useState<boolean>(false);
	const [piiLogs, setPiiLogs] = useState<Array<{ id: string; actorLabel: string; reason?: string | null; createdAt?: string; at?: string; created_at?: string }> | null>(null);

	const isCsrRestricted = !perms.has("CUSTOMERS.READ.PII") && !perms.isSuperAdmin;

	const { data: c, isLoading, isError, refetch } = useCustomer(
		id,
		isCsrRestricted && piiReason ? piiReason : undefined,
	);

	// Commandes du client
	const ordersQuery = useOrdersList(
		{ customerQuery: c?.phone ?? c?.email ?? "" },
		!!c && (activeTab === "orders" || c.stats.ordersCount > 0),
	);

	// Filtres pour l'onglet Commandes
	const [orderFilterStatus, setOrderFilterStatus] = useState<string>("all");
	const [orderSearchQuery, setOrderSearchQuery] = useState<string>("");

	const rawOrders = useMemo(() => ordersQuery.data?.items ?? [], [ordersQuery.data]);

	const filteredOrders = useMemo(() => {
		let list = rawOrders;
		if (orderFilterStatus === "pending") {
			list = list.filter((o) =>
				["PENDING_PAYMENT", "PAID", "CONFIRMED", "PROCURING", "IN_HUB", "READY_TO_SHIP", "SHIPPING"].includes(o.status)
			);
		} else if (orderFilterStatus === "delivered") {
			list = list.filter((o) => ["DELIVERED", "COMPLETED"].includes(o.status));
		} else if (orderFilterStatus === "cancelled") {
			list = list.filter((o) => ["CANCELLED", "REFUNDED", "DISPUTED"].includes(o.status));
		}

		if (orderSearchQuery.trim()) {
			const q = orderSearchQuery.trim().toLowerCase();
			list = list.filter(
				(o) =>
					o.number.toLowerCase().includes(q) ||
					(o.paymentMethod && o.paymentMethod.toLowerCase().includes(q)) ||
					o.status.toLowerCase().includes(q)
			);
		}

		return list;
	}, [rawOrders, orderFilterStatus, orderSearchQuery]);

	const adjust = useAdjustLoyalty(id ?? "");
	const addNote = useAddNote(id ?? "");

	const [noteText, setNoteText] = useState("");
	const [adjustPts, setAdjustPts] = useState("");
	const [adjustReason, setAdjustReason] = useState("");
	const [adjustApprovalId, setAdjustApprovalId] = useState("");
	const [adjustErrorMessage, setAdjustErrorMessage] = useState<string | null>(null);

	// Modales d'actions
	const [showEditModal, setShowEditModal] = useState(false);
	const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", city: "" });
	const [showSuspendModal, setShowSuspendModal] = useState(false);
	const [suspendReason, setSuspendReason] = useState("");
	const [showAnonymizeModal, setShowAnonymizeModal] = useState(false);
	const [anonymizeReason, setAnonymizeReason] = useState("");
	const [anonymizeConfirmName, setAnonymizeConfirmName] = useState("");
	const [anonymizeApprovalId, setAnonymizeApprovalId] = useState("");
	const [anonymizeConsent, setAnonymizeConsent] = useState(false);
	const [piiLogSearch, setPiiLogSearch] = useState("");
	const [newTagLabel, setNewTagLabel] = useState("");

	const formatLogDate = (log: { createdAt?: string; at?: string; created_at?: string }) => {
		const raw = log.createdAt || log.at || log.created_at;
		if (!raw) return "—";
		const d = new Date(raw);
		if (isNaN(d.getTime())) return "—";
		return d.toLocaleString("fr-FR", {
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const filteredPiiLogs = useMemo(() => {
		if (!piiLogs) return [];
		if (!piiLogSearch.trim()) return piiLogs;
		const q = piiLogSearch.toLowerCase().trim();
		return piiLogs.filter((l) => {
			const actor = (l.actorLabel || "").toLowerCase();
			const reason = (l.reason || "").toLowerCase();
			const dateStr = formatLogDate(l).toLowerCase();
			return actor.includes(q) || reason.includes(q) || dateStr.includes(q);
		});
	}, [piiLogs, piiLogSearch]);

	if (isLoading) {
		return (
			<div style={{ padding: "48px 24px", textAlign: "center" }}>
				<div className="muted" style={{ fontSize: 14 }}>Chargement de la fiche client…</div>
			</div>
		);
	}

	if (isError || !c) {
		return (
			<div style={{ padding: "48px 24px", textAlign: "center" }}>
				<AdminIcon n="alert" s={32} c="var(--a-danger, #B23A2A)" />
				<h3 style={{ marginTop: 12, marginBottom: 6 }}>Client introuvable ou accès refusé</h3>
				<p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
					Le profil recherché n'existe pas ou vous ne disposez pas des privilèges nécessaires.
				</p>
				<AdminButton variant="secondary" onClick={() => navigate("/clients")}>
					<AdminIcon n="chevL" s={14} /> Retour à la liste des clients
				</AdminButton>
			</div>
		);
	}

	const openPiiLogs = async () => {
		try {
			const logs = await customersApi.listPiiAccess(id!);
			setPiiLogs(logs);
		} catch {
			toast.error("Impossible de charger le journal des accès PII.");
		}
	};

	const handleConfirmPiiReason = () => {
		if (!piiReasonInput.trim()) {
			toast.error("Un motif de consultation PII est obligatoire pour les rôles CSR.");
			return;
		}
		setPiiReason(piiReasonInput.trim());
		setShowPiiModal(false);
		toast.info("Motif PII journalisé avec succès.");
	};

	const handleUpdateProfile = async () => {
		try {
			await customersApi.update(id!, {
				name: editForm.name || undefined,
				email: editForm.email || undefined,
				phone: editForm.phone || undefined,
				city: editForm.city || undefined,
			});
			toast.success("Fiche client mise à jour.");
			setShowEditModal(false);
			refetch();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string | string[] } } };
			const msg = ax.response?.data?.message;
			toast.error(Array.isArray(msg) ? msg.join(", ") : (msg ?? "Erreur lors de la mise à jour."));
		}
	};

	const handleSuspend = async () => {
		if (!suspendReason.trim()) {
			toast.error("Le motif de suspension est obligatoire.");
			return;
		}
		try {
			await customersApi.suspend(id!, suspendReason.trim());
			toast.success("Compte client suspendu.");
			setShowSuspendModal(false);
			setSuspendReason("");
			refetch();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			toast.error(ax.response?.data?.message ?? "Échec de la suspension.");
		}
	};

	const handleReactivate = async () => {
		try {
			await customersApi.reactivate(id!);
			toast.success("Compte client réactivé.");
			refetch();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			toast.error(ax.response?.data?.message ?? "Échec de la réactivation.");
		}
	};

	const handleAnonymize = async () => {
		if (anonymizeConfirmName.trim() !== c.name.trim()) {
			toast.error("Le nom saisi ne correspond pas exactement au nom du client.");
			return;
		}
		if (!anonymizeReason.trim()) {
			toast.error("Le motif d'anonymisation est obligatoire.");
			return;
		}
		try {
			await customersApi.anonymize(id!, anonymizeReason.trim(), anonymizeApprovalId.trim() || undefined);
			toast.success("Compte client anonymisé avec succès.");
			setShowAnonymizeModal(false);
			refetch();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			toast.error(ax.response?.data?.message ?? "Échec de l'anonymisation.");
		}
	};

	const handleAddTag = async () => {
		if (!newTagLabel.trim()) return;
		try {
			await customersApi.addTag(id!, newTagLabel.trim());
			toast.success(`Tag « ${newTagLabel} » ajouté.`);
			setNewTagLabel("");
			refetch();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			toast.error(ax.response?.data?.message ?? "Impossible d'ajouter le tag.");
		}
	};

	const handleRemoveTag = async (label: string) => {
		try {
			await customersApi.removeTag(id!, label);
			toast.success(`Tag « ${label} » retiré.`);
			refetch();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			toast.error(ax.response?.data?.message ?? "Impossible de retirer le tag.");
		}
	};

	const handleToggleConsent = async (channel: string, currentGranted: boolean) => {
		const updated = c.consents.map((cs) =>
			cs.channel === channel ? { channel: cs.channel, granted: !currentGranted } : { channel: cs.channel, granted: cs.granted },
		);
		try {
			await customersApi.updateConsents(id!, updated);
			toast.success(`Consentement ${channel.toUpperCase()} mis à jour.`);
			refetch();
		} catch (err: unknown) {
			const ax = err as { response?: { data?: { message?: string } } };
			toast.error(ax.response?.data?.message ?? "Erreur de mise à jour des consentements.");
		}
	};

	const copyToClipboard = (text: string, label: string) => {
		navigator.clipboard.writeText(text);
		toast.success(`${label} copié dans le presse-papier.`);
	};

	return (
		<>
			{/* Fil d'Ariane & Nav */}
			<div className="crumbs" style={{ marginBottom: 16, fontSize: 12 }}>
				<a
					className="root"
					style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
					onClick={() => navigate("/clients")}
				>
					<AdminIcon n="users" s={13} />
					Clients
				</a>
				<span className="sep">
					<AdminIcon n="chevR" s={10} />
				</span>
				<span className="here">{c.name}</span>
			</div>

			{/* Hero Profile Banner Card */}
			<div
				style={{
					background: "var(--a-surface, #ffffff)",
					border: "1px solid var(--a-border)",
					borderRadius: 12,
					padding: "20px 24px",
					marginBottom: 20,
					boxShadow: "var(--a-shadow-sm)",
				}}
			>
				<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
					<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
						<AdminButton
							variant="ghost"
							iconOnly
							onClick={() => navigate("/clients")}
							style={{ marginTop: -4 }}
						>
							<AdminIcon n="chevL" s={16} />
						</AdminButton>

						<div style={{ position: "relative" }}>
							<AdminAvatar name={c.name} size="lg" />
							<span
								style={{
									position: "absolute",
									bottom: 0,
									right: 0,
									width: 12,
									height: 12,
									borderRadius: "50%",
									background: c.status === "active" ? "var(--a-success, #2d7a4f)" : "var(--a-danger, #b23a2a)",
									border: "2px solid #ffffff",
								}}
							/>
						</div>

						<div>
							<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
								<h1 className="page-title" style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>
									{c.name}
								</h1>

								<Tag variant={TIER_VARIANT[c.loyalty.tier] ?? "neutral"} noDot>
									<span style={{ marginRight: 3, display: "inline-flex", alignItems: "center" }}>
										<AdminIcon n="star" s={11} />
									</span>
									{c.loyalty.tierLabel} · {c.loyalty.pointsBalance} pts
								</Tag>

								<Tag variant={c.status === "active" ? "success" : "danger"}>
									{c.status === "active" ? "Compte Actif" : "Suspendu"}
								</Tag>

								{c.piiMasked && <Tag variant="neutral"><AdminIcon n="shield" s={11} /> PII Masquée</Tag>}
							</div>

							<div className="muted mono" style={{ marginTop: 6, fontSize: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
								<span>Inscrit le {new Date(c.createdAt).toLocaleDateString("fr-FR")}</span>
								<span>•</span>
								<span>ID: #{c.id.slice(0, 8)}</span>
								<span>•</span>
								<span>{c.stats.ordersCount} commande(s)</span>
								<span>•</span>
								<span>LTV: <strong style={{ color: "var(--a-ink)" }}>{fmtFCFA(c.stats.ltv)}</strong></span>
							</div>
						</div>
					</div>

					{/* Actions toolbar */}
					<div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
						{isCsrRestricted && c.piiMasked && (
							<AdminButton variant="ghost" onClick={() => setShowPiiModal(true)}>
								<AdminIcon n="shield" s={14} /> Saisir motif PII
							</AdminButton>
						)}
						{perms.has("SYSTEM.AUDIT.READ") && (
							<AdminButton variant="ghost" onClick={openPiiLogs}>
								<AdminIcon n="clock" s={14} /> Journal PII
							</AdminButton>
						)}
						<AdminButton
							variant="secondary"
							onClick={() => {
								setEditForm({
									name: c.name,
									email: c.email ?? "",
									phone: c.phone,
									city: c.addresses?.[0]?.city ?? "",
								});
								setShowEditModal(true);
							}}
						>
							<AdminIcon n="edit" s={14} /> Éditer profil
						</AdminButton>

						{c.status === "active" ? (
							<AdminButton variant="ghost" onClick={() => setShowSuspendModal(true)}>
								<AdminIcon n="lock" s={14} /> Suspendre
							</AdminButton>
						) : (
							<AdminButton variant="primary" onClick={handleReactivate}>
								<AdminIcon n="check" s={14} /> Réactiver
							</AdminButton>
						)}

						<AdminButton
							variant="secondary"
							className="text-red-600"
							onClick={() => {
								setAnonymizeReason("");
								setAnonymizeConfirmName("");
								setAnonymizeApprovalId("");
								setAnonymizeConsent(false);
								setShowAnonymizeModal(true);
							}}
						>
							<AdminIcon n="trash" s={14} /> Anonymiser (RGPD)
						</AdminButton>
					</div>
				</div>
			</div>

			{/* KPI Grid */}
			<div className="kpi-grid" style={{ marginBottom: 20 }}>
				<Kpi label="Valeur Vie Client (LTV)" variant="accent" value={fmtFCFA(c.stats.ltv)} />
				<Kpi label="Panier Moyen (AOV)" value={fmtFCFA(c.stats.aov)} />
				<Kpi label="Solde Fidélité" variant="forest" value={`${c.loyalty.pointsBalance} pts`} />
				<Kpi label="Filleuls Parrainés" value={`${c.referral.sponsoredCount} membres`} />
			</div>

			{/* Custom Tab Navigation Bar */}
			<div
				style={{
					display: "flex",
					gap: 4,
					borderBottom: "1px solid var(--a-border)",
					marginBottom: 20,
					paddingBottom: 2,
				}}
			>
				<button
					type="button"
					onClick={() => setActiveTab("overview")}
					style={{
						padding: "10px 18px",
						background: "none",
						border: "none",
						borderBottom: activeTab === "overview" ? "2px solid var(--a-primary, #c6563a)" : "2px solid transparent",
						color: activeTab === "overview" ? "var(--a-primary, #c6563a)" : "var(--a-ink-soft)",
						fontWeight: activeTab === "overview" ? 700 : 500,
						fontSize: 13,
						cursor: "pointer",
						display: "inline-flex",
						alignItems: "center",
						gap: 6,
						transition: "all 0.15s ease",
					}}
				>
					<AdminIcon n="user" s={14} /> Vue d'ensemble
				</button>

				<button
					type="button"
					onClick={() => setActiveTab("orders")}
					style={{
						padding: "10px 18px",
						background: "none",
						border: "none",
						borderBottom: activeTab === "orders" ? "2px solid var(--a-primary, #c6563a)" : "2px solid transparent",
						color: activeTab === "orders" ? "var(--a-primary, #c6563a)" : "var(--a-ink-soft)",
						fontWeight: activeTab === "orders" ? 700 : 500,
						fontSize: 13,
						cursor: "pointer",
						display: "inline-flex",
						alignItems: "center",
						gap: 6,
						transition: "all 0.15s ease",
					}}
				>
					<AdminIcon n="cart" s={14} /> Commandes & Achats ({c.stats.ordersCount})
				</button>

				<button
					type="button"
					onClick={() => setActiveTab("loyalty")}
					style={{
						padding: "10px 18px",
						background: "none",
						border: "none",
						borderBottom: activeTab === "loyalty" ? "2px solid var(--a-primary, #c6563a)" : "2px solid transparent",
						color: activeTab === "loyalty" ? "var(--a-primary, #c6563a)" : "var(--a-ink-soft)",
						fontWeight: activeTab === "loyalty" ? 700 : 500,
						fontSize: 13,
						cursor: "pointer",
						display: "inline-flex",
						alignItems: "center",
						gap: 6,
						transition: "all 0.15s ease",
					}}
				>
					<AdminIcon n="star" s={14} /> Fidélité & Parrainage
				</button>

				<button
					type="button"
					onClick={() => setActiveTab("security")}
					style={{
						padding: "10px 18px",
						background: "none",
						border: "none",
						borderBottom: activeTab === "security" ? "2px solid var(--a-primary, #c6563a)" : "2px solid transparent",
						color: activeTab === "security" ? "var(--a-primary, #c6563a)" : "var(--a-ink-soft)",
						fontWeight: activeTab === "security" ? 700 : 500,
						fontSize: 13,
						cursor: "pointer",
						display: "inline-flex",
						alignItems: "center",
						gap: 6,
						transition: "all 0.15s ease",
					}}
				>
					<AdminIcon n="shield" s={14} /> Sécurité & RGPD
				</button>
			</div>

			{/* TAB 1: VUE D'ENSEMBLE */}
			{activeTab === "overview" && (
				<div className="grid-2">
					<div className="stack">
						{/* Card Identité & contacts */}
						<Card>
							<CardHead title="Identité & Coordonnées" sub="Informations de contact et d'acquisition" />
							<CardBody>
								<div style={{ display: "grid", gap: 4 }}>
									<RowWithAction
										label="Téléphone"
										value={c.phone}
										mono
										onCopy={() => copyToClipboard(c.phone, "Numéro de téléphone")}
									/>
									<RowWithAction
										label="Email"
										value={c.email ?? "—"}
										mono
										onCopy={c.email ? () => copyToClipboard(c.email!, "Email") : undefined}
									/>
									<Row label="Type client" value={c.type.toUpperCase()} />
									<Row label="Source d'acquisition" value={c.acquisitionSource ?? "Recherche directe / Organique"} />
									<RowWithAction
										label="Code parrain"
										value={c.referral.code}
										mono
										onCopy={() => copyToClipboard(c.referral.code, "Code parrain")}
									/>

									{c.contacts.length > 0 && (
										<div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--a-border)" }}>
											<span className="muted" style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>
												Contacts secondaires
											</span>
											<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
												{c.contacts.map((ct) => (
													<Tag key={ct.id} variant="neutral" noDot>
														{ct.type}: {ct.value}
													</Tag>
												))}
											</div>
										</div>
									)}
								</div>
							</CardBody>
						</Card>

						{/* Card Tags Client */}
						<Card>
							<CardHead title="Tags & Étiquettes" sub="Segmentation manuelle du profil" />
							<CardBody>
								<div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12, minHeight: 32 }}>
									{c.tags.length === 0 && <span className="muted" style={{ fontSize: 12 }}>Aucun tag actuellement rattaché à ce profil.</span>}
									{c.tags.map((t) => (
										<Tag key={t.id} variant="info" noDot>
											{t.label}
											<button
												type="button"
												onClick={() => handleRemoveTag(t.label)}
												style={{ marginLeft: 6, cursor: "pointer", background: "none", border: "none", opacity: 0.7, padding: 0 }}
												title="Supprimer ce tag"
											>
												<AdminIcon n="close" s={10} />
											</button>
										</Tag>
									))}
								</div>
								<div style={{ display: "flex", gap: 8 }}>
									<input
										className="inp"
										placeholder="Nouveau tag (ex: VIP, VIP_GROSSISTE)…"
										value={newTagLabel}
										onChange={(e) => setNewTagLabel(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter") handleAddTag();
										}}
										style={{ flex: 1 }}
									/>
									<AdminButton variant="secondary" onClick={handleAddTag} disabled={!newTagLabel.trim()}>
										<AdminIcon n="plus" s={14} /> Ajouter
									</AdminButton>
								</div>
							</CardBody>
						</Card>

						{/* Card Adresses */}
						<Card>
							<CardHead title={`Carnet d'Adresses (${c.addresses.length})`} />
							<CardBody>
								{c.addresses.length === 0 ? (
									<div
										className="muted"
										style={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											padding: "24px 16px",
											textAlign: "center",
											fontSize: 13,
										}}
									>
										<AdminIcon n="pin" s={22} c="var(--a-ink-mute)" />
										<div style={{ marginTop: 8 }}>Aucune adresse enregistrée pour ce client.</div>
									</div>
								) : (
									<div style={{ display: "grid", gap: 8, maxHeight: 260, overflowY: "auto", paddingRight: 4 }}>
										{c.addresses.map((a) => (
											<div
												key={a.id}
												style={{
													padding: "10px 12px",
													background: "var(--a-app-bg, #f9f6ef)",
													border: "1px solid var(--a-border)",
													borderRadius: 8,
													display: "flex",
													justifyContent: "space-between",
													alignItems: "flex-start",
													gap: 12,
												}}
											>
												<div style={{ flex: 1, minWidth: 0 }}>
													<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
														<strong style={{ fontSize: 13, textTransform: "capitalize", color: "var(--a-ink)" }}>
															{a.label || (a.type === "shipping" ? "Adresse de livraison" : a.type === "billing" ? "Adresse de facturation" : a.type)}
														</strong>
														{a.phone && (
															<span
																className="mono muted"
																style={{
																	fontSize: 11,
																	display: "inline-flex",
																	alignItems: "center",
																	gap: 4,
																	background: "#ffffff",
																	padding: "2px 6px",
																	borderRadius: 4,
																	border: "1px solid var(--a-border)",
																}}
															>
																<AdminIcon n="phone" s={11} c="var(--a-ink-soft)" />
																{a.phone}
															</span>
														)}
													</div>
													<div className="muted" style={{ fontSize: 12, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
														<AdminIcon n="pin" s={12} c="var(--a-ink-mute)" />
														<span>
															{[a.quarter, a.city].filter(Boolean).join(", ") || "—"}
															{a.street ? ` · ${a.street}` : ""}
														</span>
													</div>
													{a.recipientName && (
														<div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
															Destinataire : <strong>{a.recipientName}</strong>
														</div>
													)}
												</div>
												<Tag variant="neutral" noDot style={{ fontSize: 10, textTransform: "uppercase", flexShrink: 0 }}>
													{a.type}
												</Tag>
											</div>
										))}
									</div>
								)}
							</CardBody>
						</Card>
					</div>

					<div className="stack">
						{/* Card Notes internes (Timeline style) */}
						<Card>
							<CardHead title={`Notes Internes (${c.notes.length})`} sub="Échanges et remarques de l'équipe" />
							<CardBody>
								<div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
									<input
										className="inp"
										placeholder="Ajouter une note de suivi..."
										value={noteText}
										onChange={(e) => setNoteText(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter" && noteText) {
												addNote.mutate(noteText, {
													onSuccess: () => {
														toast.success("Note ajoutée.");
														setNoteText("");
														refetch();
													},
												});
											}
										}}
										style={{ flex: 1 }}
									/>
									<AdminButton
										variant="primary"
										disabled={addNote.isPending || !noteText.trim()}
										onClick={() =>
											addNote.mutate(noteText, {
												onSuccess: () => {
													toast.success("Note ajoutée.");
													setNoteText("");
													refetch();
												},
											})
										}
									>
										Ajouter
									</AdminButton>
								</div>

								{c.notes.length === 0 ? (
									<div className="muted" style={{ textAlign: "center", padding: "16px 0", fontSize: 13 }}>
										Aucune note enregistrée sur ce profil client.
									</div>
								) : (
									<div style={{ display: "grid", gap: 10, maxHeight: 360, overflowY: "auto" }}>
										{c.notes.map((n) => (
											<div
												key={n.id}
												style={{
													padding: 10,
													border: "1px solid var(--a-border)",
													borderRadius: 8,
													background: "#ffffff",
												}}
											>
												<div style={{ fontSize: 13, color: "var(--a-ink)" }}>{n.content}</div>
												<div className="muted mono" style={{ fontSize: 11, marginTop: 6, display: "flex", justifyContent: "space-between" }}>
													<span>Par : <strong>{n.authorLabel ?? "Système"}</strong></span>
													<span>{new Date(n.createdAt).toLocaleString("fr-FR")}</span>
												</div>
											</div>
										))}
									</div>
								)}
							</CardBody>
						</Card>

						{/* Card Consentements Marketing */}
						<Card>
							<CardHead title="Consentements & Opt-In" sub="Conformité communication (Loi 2010/012)" />
							<CardBody>
								<div style={{ display: "grid", gap: 8, maxHeight: 260, overflowY: "auto", paddingRight: 4 }}>
									{c.consents.map((cs) => {
										const icn = CHANNEL_ICON[cs.channel.toLowerCase()] ?? "mail";
										return (
											<div
												key={cs.id}
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													padding: "8px 10px",
													background: "var(--a-app-bg, #f9f6ef)",
													border: "1px solid var(--a-border)",
													borderRadius: 8,
												}}
											>
												<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
													<AdminIcon n={icn} s={15} c="var(--a-ink-secondary)" />
													<div>
														<strong style={{ fontSize: 12, textTransform: "uppercase" }}>Canal {cs.channel}</strong>
														<div className="muted" style={{ fontSize: 11 }}>
															{cs.granted ? "Accepté par le client" : "Refusé / Non renseigné"}
														</div>
													</div>
												</div>

												<button
													type="button"
													onClick={() => handleToggleConsent(cs.channel, cs.granted)}
													style={{
														cursor: "pointer",
														background: "none",
														border: "none",
														padding: 0,
													}}
												>
													<Tag variant={cs.granted ? "success" : "neutral"} noDot style={{ fontSize: 11 }}>
														{cs.granted ? "✓ Accordé" : "✕ Refusé"}
													</Tag>
												</button>
											</div>
										);
									})}
								</div>
							</CardBody>
						</Card>
					</div>
				</div>
			)}

			{/* TAB 2: COMMANDES & ACHATS */}
			{activeTab === "orders" && (
				<Card style={{ overflow: "hidden" }}>
					<CardHead
						title={`Historique des Commandes (${c.stats.ordersCount})`}
						sub="Consultez et suivez l'historique complet des achats du client"
						right={
							<AdminButton
								variant="ghost"
								size="sm"
								onClick={() => navigate(`/commandes/nouvelle`)}
							>
								<AdminIcon n="plus" s={12} /> Nouvelle commande
							</AdminButton>
						}
					/>

					{/* Quick Filter Bar & Search */}
					<div
						style={{
							padding: "12px 20px",
							borderBottom: "1px solid var(--a-border)",
							background: "var(--a-surface-2, #f9f6ef)",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 12,
							flexWrap: "wrap",
						}}
					>
						<div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
							{[
								{ key: "all", label: `Toutes (${rawOrders.length})` },
								{
									key: "pending",
									label: `En cours (${rawOrders.filter((o) => ["PENDING_PAYMENT", "PAID", "CONFIRMED", "PROCURING", "IN_HUB", "READY_TO_SHIP", "SHIPPING"].includes(o.status)).length})`,
								},
								{
									key: "delivered",
									label: `Livrées (${rawOrders.filter((o) => ["DELIVERED", "COMPLETED"].includes(o.status)).length})`,
								},
								{
									key: "cancelled",
									label: `Annulées / Litiges (${rawOrders.filter((o) => ["CANCELLED", "REFUNDED", "DISPUTED"].includes(o.status)).length})`,
								},
							].map((tabItem) => (
								<button
									key={tabItem.key}
									type="button"
									onClick={() => setOrderFilterStatus(tabItem.key)}
									className={`chip-filter ${orderFilterStatus === tabItem.key ? "on" : ""}`}
									style={{ cursor: "pointer" }}
								>
									{tabItem.label}
								</button>
							))}
						</div>

						{rawOrders.length > 0 && (
							<div style={{ display: "inline-flex", alignItems: "center", position: "relative", width: 240, flexShrink: 0 }}>
								<span
									style={{
										position: "absolute",
										left: 10,
										top: "50%",
										transform: "translateY(-50%)",
										display: "inline-flex",
										alignItems: "center",
										justifyContent: "center",
										pointerEvents: "none",
										color: "var(--a-ink-mute)",
										zIndex: 2,
									}}
								>
									<AdminIcon n="search" s={13} />
								</span>
								<input
									type="text"
									className="inp"
									placeholder="Rechercher par N° commande…"
									value={orderSearchQuery}
									onChange={(e) => setOrderSearchQuery(e.target.value)}
									style={{
										paddingLeft: 30,
										paddingRight: 10,
										fontSize: 12,
										height: 32,
										borderRadius: 6,
										width: "100%",
									}}
								/>
							</div>
						)}
					</div>

					<CardBody style={{ padding: 0 }}>
						{ordersQuery.isLoading ? (
							<div style={{ padding: "40px 24px", textAlign: "center" }} className="muted">
								<span style={{ marginBottom: 8, display: "inline-block" }}>
									<AdminIcon n="clock" s={24} />
								</span>
								<div>Chargement de l'historique des commandes…</div>
							</div>
						) : !ordersQuery.data || rawOrders.length === 0 ? (
							<div style={{ padding: "48px 24px", textAlign: "center" }}>
								<div
									style={{
										width: 48,
										height: 48,
										borderRadius: "50%",
										background: "var(--a-surface-2)",
										display: "grid",
										placeItems: "center",
										margin: "0 auto 12px",
									}}
								>
									<AdminIcon n="cart" s={24} c="var(--a-ink-mute)" />
								</div>
								<h4 style={{ margin: "0 0 6px", fontWeight: 700 }}>Aucune commande enregistrée</h4>
								<p className="muted" style={{ fontSize: 13, margin: 0, maxWidth: 360, marginInline: "auto" }}>
									Ce client n'a pas encore passé de commande sur la plateforme.
								</p>
							</div>
						) : filteredOrders.length === 0 ? (
							<div style={{ padding: "48px 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
								<div
									style={{
										width: 44,
										height: 44,
										borderRadius: "50%",
										background: "var(--a-surface-2)",
										display: "grid",
										placeItems: "center",
										margin: "0 auto 12px",
									}}
								>
									<AdminIcon n="search" s={20} c="var(--a-ink-mute)" />
								</div>
								<div className="muted" style={{ fontSize: 13, margin: "0 0 12px" }}>
									Aucune commande ne correspond aux filtres sélectionnés.
								</div>
								<AdminButton
									variant="ghost"
									size="sm"
									onClick={() => {
										setOrderFilterStatus("all");
										setOrderSearchQuery("");
									}}
								>
									Réinitialiser les filtres
								</AdminButton>
							</div>
						) : (
							<div style={{ overflowX: "auto" }}>
								<table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
									<thead>
										<tr style={{ background: "var(--a-surface-2)" }}>
											<th style={{ padding: "11px 16px 11px 20px", width: "18%" }}>N° Commande</th>
											<th style={{ padding: "11px 16px", width: "22%" }}>Date & Heure</th>
											<th style={{ padding: "11px 16px", width: "12%" }}>Articles</th>
											<th style={{ padding: "11px 16px", width: "20%" }}>Mode & Paiement</th>
											<th style={{ padding: "11px 16px", width: "16%" }}>Statut</th>
											<th style={{ padding: "11px 16px", width: "12%" }}>Total</th>
											<th style={{ padding: "11px 20px 11px 16px", textAlign: "right", width: "10%" }}>Action</th>
										</tr>
									</thead>
									<tbody>
										{filteredOrders.map((o) => {
											const st = statusOrderV2(o.status);
											const subLabel = ORDER_STATUS_SUB_LABEL[o.status] ?? "";
											const payMethod = o.paymentMethod
												? PAYMENT_METHOD_LABEL[o.paymentMethod] ?? o.paymentMethod
												: "Non spécifié";
											const payStatusLabel = PAYMENT_STATUS_LABEL[o.paymentStatus] ?? o.paymentStatus;
											const isPaid = o.paymentStatus === "CAPTURED" || o.paymentStatus === "AUTHORIZED";

											return (
												<tr
													key={o.id}
													style={{ cursor: "pointer" }}
													onClick={() => navigate(`/commandes/${o.id}`)}
												>
													<td style={{ padding: "12px 16px 12px 20px" }}>
														<div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
															<strong className="mono" style={{ color: "var(--a-terracotta)", fontWeight: 700 }}>
																{o.number}
															</strong>
															{o.source && (
																<span className="muted" style={{ fontSize: 10 }}>
																	{o.source === "WEB" ? "Site Web" : o.source === "APP" ? "App Mobile" : o.source}
																</span>
															)}
														</div>
													</td>
													<td className="muted mono" style={{ padding: "12px 16px", fontSize: 12, whiteSpace: "nowrap" }}>
														<div style={{ fontWeight: 500, color: "var(--a-ink)" }}>
															{new Date(o.createdAt).toLocaleDateString("fr-FR", {
																day: "2-digit",
																month: "short",
																year: "numeric",
															})}
														</div>
														<div style={{ fontSize: 11, opacity: 0.8 }}>
															{new Date(o.createdAt).toLocaleTimeString("fr-FR", {
																hour: "2-digit",
																minute: "2-digit",
															})}
														</div>
													</td>
													<td style={{ padding: "12px 16px" }}>
														<Tag variant="neutral" noDot style={{ fontSize: 11 }}>
															{o.lineCount ?? o.lines?.length ?? 1} art.
														</Tag>
													</td>
													<td style={{ padding: "12px 16px" }}>
														<div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
															<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-2)" }}>
																{payMethod}
															</span>
															<span
																className="mono"
																style={{
																	fontSize: 10,
																	color: isPaid ? "var(--a-success)" : "var(--a-ink-mute)",
																	fontWeight: isPaid ? 600 : 400,
																}}
															>
																{isPaid ? "✓ " : "• "}{payStatusLabel}
															</span>
														</div>
													</td>
													<td style={{ padding: "12px 16px" }}>
														<div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
															<Tag variant={st.cls}>{st.l}</Tag>
															{subLabel && subLabel !== st.l && (
																<span className="muted" style={{ fontSize: 10 }}>
																	{subLabel}
																</span>
															)}
														</div>
													</td>
													<td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
														<strong className="mono" style={{ fontSize: 13, color: "var(--a-ink)" }}>
															{fmtFCFA(o.totalAmount)}
														</strong>
													</td>
													<td style={{ padding: "12px 20px 12px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
														<AdminButton
															variant="ghost"
															size="sm"
															onClick={(e) => {
																e.stopPropagation();
																navigate(`/commandes/${o.id}`);
															}}
															title="Consulter la commande"
														>
															Consulter <AdminIcon n="chevR" s={12} />
														</AdminButton>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</CardBody>
				</Card>
			)}

			{/* TAB 3: FIDÉLITÉ & PARRAINAGE */}
			{activeTab === "loyalty" && (
				<div className="grid-2">
					<div className="stack">
						{/* Card Statut Fidélité */}
						<Card style={{ overflow: "hidden" }}>
							<CardHead
								title="Programme Akwaba Rewards"
								sub={`Palier actuel : ${c.loyalty.tierLabel}`}
								right={
									<Tag variant={TIER_VARIANT[c.loyalty.tier] ?? "neutral"} noDot style={{ fontWeight: 700 }}>
										<span style={{ marginRight: 4, display: "inline-flex", alignItems: "center" }}>
											<AdminIcon n="star" s={12} />
										</span>
										{c.loyalty.tierLabel.toUpperCase()}
									</Tag>
								}
							/>
							<CardBody style={{ display: "grid", gap: 16 }}>
								{/* Main Points Banner */}
								<div
									style={{
										background: "linear-gradient(135deg, var(--a-surface-2, #f2eee3) 0%, var(--a-app-bg, #f9f6ef) 100%)",
										border: "1px solid var(--a-border)",
										borderRadius: 10,
										padding: "16px 18px",
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										gap: 16,
										flexWrap: "wrap",
									}}
								>
									<div>
										<span className="eyebrow" style={{ color: "var(--a-ink-mute)", fontSize: 11 }}>
											Solde de points utilisables
										</span>
										<div className="mono" style={{ fontSize: 26, fontWeight: 900, color: "var(--a-terracotta)", lineHeight: 1.1, marginTop: 2 }}>
											{c.loyalty.pointsBalance} <span style={{ fontSize: 14, fontWeight: 600 }}>pts</span>
										</div>
										<div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
											Valeur estimée : <strong>~{fmtFCFA(c.loyalty.pointsBalance * 5)}</strong> de réduction
										</div>
									</div>
									<div
										style={{
											width: 44,
											height: 44,
											borderRadius: "50%",
											background: "var(--a-terra-soft, #fbe8e0)",
											color: "var(--a-terracotta)",
											display: "grid",
											placeItems: "center",
											flexShrink: 0,
										}}
									>
										<AdminIcon n="star" s={22} />
									</div>
								</div>

								{/* Details Table */}
								<div style={{ display: "grid", gap: 6, fontSize: 13 }}>
									<Row label="Palier de fidélité" value={c.loyalty.tierLabel} />
									<Row label="Points cumulés (Lifetime)" value={`${c.loyalty.pointsLifetime} pts`} mono />
									<Row
										label="Membre du palier depuis"
										value={c.loyalty.tierSince ? new Date(c.loyalty.tierSince).toLocaleDateString("fr-FR") : "—"}
									/>
								</div>

								{/* Progress towards Next Tier */}
								{c.loyalty.nextTierProgress && (
									<div
										style={{
											padding: 14,
											background: "#ffffff",
											borderRadius: 10,
											border: "1px solid var(--a-border)",
											display: "grid",
											gap: 8,
										}}
									>
										<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
											<span style={{ fontSize: 12, fontWeight: 700, color: "var(--a-ink)" }}>
												Progression du statut (12 derniers mois)
											</span>
											<Tag variant="neutral" noDot style={{ fontSize: 10 }}>
												Activité glissante
											</Tag>
										</div>

										<div className="muted mono" style={{ fontSize: 12, display: "flex", justifyContent: "space-between", gap: 8 }}>
											<span>📦 {c.loyalty.nextTierProgress.orders_12m} commande(s) réalisées</span>
											<span>💰 {fmtFCFA(c.loyalty.nextTierProgress.revenue_12m)}</span>
										</div>

										{/* Visual progress bar */}
										<div
											style={{
												width: "100%",
												height: 8,
												background: "var(--a-surface-2, #f2eee3)",
												borderRadius: 4,
												overflow: "hidden",
												marginTop: 2,
											}}
										>
											<div
												style={{
													width: `${Math.min(100, Math.max(15, (c.loyalty.nextTierProgress.orders_12m / 5) * 100))}%`,
													height: "100%",
													background: "linear-gradient(90deg, var(--a-terracotta) 0%, var(--a-ochre) 100%)",
													borderRadius: 4,
													transition: "width 0.3s ease",
												}}
											/>
										</div>
									</div>
								)}
							</CardBody>
						</Card>

						{/* Card Parrainage */}
						<Card>
							<CardHead
								title="Programme de Parrainage"
								sub="Filleuls et code de parrainage client"
								right={
									<Tag variant="info" noDot>
										<span style={{ marginRight: 3, display: "inline-flex", alignItems: "center" }}>
											<AdminIcon n="users" s={11} />
										</span>
										{c.referral.sponsoredCount} filleul(s)
									</Tag>
								}
							/>
							<CardBody style={{ display: "grid", gap: 14 }}>
								<div style={{ display: "grid", gap: 8 }}>
									<RowWithAction
										label="Code Parrain Client"
										value={c.referral.code}
										mono
										onCopy={() => copyToClipboard(c.referral.code, "Code parrain")}
									/>

									<RowWithAction
										label="Lien d'invitation direct"
										value={`https://akwaba.shop/ref/${c.referral.code}`}
										mono
										onCopy={() => copyToClipboard(`https://akwaba.shop/ref/${c.referral.code}`, "Lien de parrainage")}
									/>

									<Row label="Filleuls parrainés avec succès" value={`${c.referral.sponsoredCount} personnes`} />
								</div>

								{/* Referral Advantage Box */}
								<div
									style={{
										padding: "12px 14px",
										background: "var(--a-forest-soft, #e2ebe5)",
										border: "1px solid rgba(45, 122, 79, 0.2)",
										borderRadius: 8,
										display: "flex",
										alignItems: "flex-start",
										gap: 10,
									}}
								>
									<span style={{ marginTop: 1, flexShrink: 0, display: "inline-flex" }}>
										<AdminIcon n="star" s={18} c="var(--a-forest, #1e4d3b)" />
									</span>
									<div style={{ fontSize: 12, color: "var(--a-forest, #1e4d3b)", lineHeight: 1.4 }}>
										<strong>Bonus de Parrainage Akwaba :</strong>
										<br />
										Chaque filleul parrainé offre <strong>+500 points</strong> (~2 500 FCFA) au parrain et au filleul dès sa 1ère commande validée.
									</div>
								</div>
							</CardBody>
						</Card>
					</div>

					<div className="stack">
						{/* Formulaire Ajustement de points */}
						<Card>
							<CardHead
								title="Ajustement Manuel de Points (4-Eyes)"
								sub="Crédit ou débit exceptionnel de points soumis au contrôle RBAC"
								right={
									<Tag variant="warning" noDot>
										<span style={{ marginRight: 3, display: "inline-flex", alignItems: "center" }}>
											<AdminIcon n="shield" s={11} />
										</span>
										Gouvernance 4-Eyes
									</Tag>
								}
							/>
							<CardBody>
								<div style={{ display: "grid", gap: 14 }}>
									{/* Security Banner */}
									<div
										style={{
											padding: "10px 12px",
											background: "var(--a-warning-soft, #f8eac8)",
											border: "1px solid var(--a-border)",
											borderRadius: 8,
											fontSize: 12,
											color: "var(--a-warning, #b8780e)",
											display: "flex",
											alignItems: "center",
											gap: 8,
										}}
									>
										<AdminIcon n="lock" s={15} c="var(--a-warning, #b8780e)" />
										<span>Toute modification de points est tracée en audit. Un motif valide est obligatoire.</span>
									</div>

									<div>
										<label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6, color: "var(--a-ink)" }}>
											Nombre de points (+/-)
										</label>
										<div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
											{[
												{ value: "+50", isPos: true },
												{ value: "+100", isPos: true },
												{ value: "+500", isPos: true },
												{ value: "-50", isPos: false },
												{ value: "-100", isPos: false },
											].map((preset) => {
												const isSelected = adjustPts === preset.value;
												return (
													<button
														key={preset.value}
														type="button"
														onClick={() => setAdjustPts(preset.value)}
														style={{
															padding: "5px 12px",
															background: isSelected
																? preset.isPos
																	? "var(--a-success, #2d7a4f)"
																	: "var(--a-danger, #b23a2a)"
																: preset.isPos
																	? "var(--a-success-soft, #ddeae0)"
																	: "var(--a-danger-soft, #f8d9d2)",
															color: isSelected
																? "#ffffff"
																: preset.isPos
																	? "var(--a-success, #2d7a4f)"
																	: "var(--a-danger, #b23a2a)",
															border: isSelected
																? "1px solid transparent"
																: preset.isPos
																	? "1px solid rgba(45, 122, 79, 0.3)"
																	: "1px solid rgba(178, 58, 42, 0.3)",
															borderRadius: 6,
															fontSize: 12,
															cursor: "pointer",
															fontWeight: 700,
															transition: "all 0.15s ease",
														}}
													>
														{preset.value} pts
													</button>
												);
											})}
										</div>
										<input
											className="inp mono"
											placeholder="Ex: 100 ou -50"
											value={adjustPts}
											onChange={(e) => setAdjustPts(e.target.value)}
										/>
									</div>

									<div>
										<label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4, color: "var(--a-ink)" }}>
											Motif obligatoire *
										</label>
										<input
											className="inp"
											placeholder="Ex : Geste commercial suite à retard de livraison"
											value={adjustReason}
											onChange={(e) => setAdjustReason(e.target.value)}
										/>
									</div>

									<div>
										<label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4, color: "var(--a-ink)" }}>
											ID de validation 4-eyes (si dépassement de votre plafond)
										</label>
										<input
											className="inp mono"
											placeholder="ID d'approbation manager..."
											value={adjustApprovalId}
											onChange={(e) => setAdjustApprovalId(e.target.value)}
										/>
									</div>

									<AdminButton
										variant="primary"
										disabled={adjust.isPending || !adjustPts || !adjustReason.trim()}
										onClick={() => {
											setAdjustErrorMessage(null);
											adjust.mutate(
												{
													points: Number(adjustPts),
													reason: adjustReason.trim(),
													approvalId: adjustApprovalId.trim() || undefined,
												},
												{
													onSuccess: () => {
														toast.success("Ajustement de points appliqué.");
														setAdjustPts("");
														setAdjustReason("");
														setAdjustApprovalId("");
														refetch();
													},
													onError: (err: unknown) => {
														const ax = err as { response?: { data?: { message?: string } } };
														const msg = ax.response?.data?.message ?? "Refusé (plafond du rôle ou validation 4-eyes requise).";
														setAdjustErrorMessage(msg);
														toast.error(msg);
													},
												},
											);
										}}
										style={{ marginTop: 4, width: "100%", justifyContent: "center" }}
									>
										<AdminIcon n="check" s={14} />
										{adjust.isPending ? "Traitement en cours…" : "Confirmer l'ajustement de points"}
									</AdminButton>

									{adjustErrorMessage && (
										<div
											style={{
												padding: "8px 12px",
												background: "var(--a-danger-soft, #f8d9d2)",
												border: "1px solid rgba(178, 58, 42, 0.3)",
												borderRadius: 6,
												color: "var(--a-danger, #B23A2A)",
												fontSize: 12,
												display: "flex",
												alignItems: "center",
												gap: 6,
											}}
										>
											<AdminIcon n="alert" s={14} c="var(--a-danger)" />
											<span>{adjustErrorMessage}</span>
										</div>
									)}
								</div>
							</CardBody>
						</Card>
					</div>
				</div>
			)}

			{/* TAB 4: SÉCURITÉ & RGPD */}
			{activeTab === "security" && (
				<div className="grid-2">
					<div className="stack">
						{/* Card Données Personnelles & PII */}
						<Card>
							<CardHead
								title="Données Personnelles & PII"
								sub="Contrôle d'accès, masquage et journal de sécurité"
								right={
									<Tag variant={c.piiMasked ? "neutral" : "success"} noDot>
										<span style={{ marginRight: 3, display: "inline-flex", alignItems: "center" }}>
											<AdminIcon n="shield" s={11} />
										</span>
										{c.piiMasked ? "PII Masquée" : "PII Visible"}
									</Tag>
								}
							/>
							<CardBody style={{ display: "grid", gap: 16 }}>
								{/* Visual Status Box */}
								<div
									style={{
										padding: "14px 16px",
										background: piiReason ? "var(--a-info-soft, #dce8f0)" : "var(--a-surface-2, #f2eee3)",
										border: "1px solid var(--a-border)",
										borderRadius: 10,
										display: "flex",
										alignItems: "flex-start",
										gap: 12,
									}}
								>
									<div
										style={{
											width: 36,
											height: 36,
											borderRadius: 8,
											background: piiReason ? "var(--a-info, #2b6a8e)" : "var(--a-surface)",
											color: piiReason ? "#ffffff" : "var(--a-ink-secondary)",
											display: "grid",
											placeItems: "center",
											flexShrink: 0,
										}}
									>
										<AdminIcon n={piiReason ? "key" : "lock"} s={18} />
									</div>

									<div style={{ flex: 1, minWidth: 0 }}>
										<div style={{ fontSize: 13, fontWeight: 700, color: "var(--a-ink)" }}>
											{piiReason ? "Accès PII Débloqué (Session CSR Active)" : "Masquage Réglementaire des Données PII"}
										</div>
										<p className="muted" style={{ fontSize: 12, margin: "3px 0 0 0", lineHeight: 1.4 }}>
											{piiReason
												? `Motif consigné : "${piiReason}"`
												: "La consultation des données identifiantes (nom, email, téléphone) de ce client est tracée et soumise au contrôle RBAC (Rôle & Permissions)."}
										</p>
									</div>
								</div>

								{/* Action Buttons */}
								<div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
									{perms.has("SYSTEM.AUDIT.READ") && (
										<AdminButton variant="secondary" onClick={openPiiLogs}>
											<AdminIcon n="clock" s={14} /> Consulter le journal PII
										</AdminButton>
									)}

									{isCsrRestricted && (
										<AdminButton variant="ghost" onClick={() => setShowPiiModal(true)}>
											<AdminIcon n="shield" s={14} /> Saisir un nouveau motif PII
										</AdminButton>
									)}
								</div>

								{/* Section Consentements Client */}
								<div style={{ borderTop: "1px solid var(--a-border)", paddingTop: 14 }}>
									<span style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 8, color: "var(--a-ink)" }}>
										État des Consentements & Directives (Opt-In)
									</span>
									<div style={{ display: "grid", gap: 6 }}>
										{c.consents.map((cs) => {
											const icn = CHANNEL_ICON[cs.channel.toLowerCase()] ?? "mail";
											return (
												<div
													key={cs.id}
													style={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
														padding: "8px 12px",
														background: "var(--a-app-bg, #f9f6ef)",
														border: "1px solid var(--a-border)",
														borderRadius: 6,
													}}
												>
													<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
														<AdminIcon n={icn} s={14} c="var(--a-ink-secondary)" />
														<span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase" }}>Canal {cs.channel}</span>
													</div>
													<Tag variant={cs.granted ? "success" : "neutral"} noDot style={{ fontSize: 11 }}>
														{cs.granted ? "✓ Opt-in accordé" : "✕ Refusé"}
													</Tag>
												</div>
											);
										})}
									</div>
								</div>

								{/* Section Conformité Informatique & Libertés */}
								<div style={{ background: "var(--a-app-bg, #f9f6ef)", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--a-border)", fontSize: 11, color: "var(--a-ink-soft)" }}>
									<div style={{ fontWeight: 700, marginBottom: 4, color: "var(--a-ink)" }}>
										📜 Conformité Loi N° 2013-450 & RGPD :
									</div>
									Le client dispose d'un droit d'accès, de rectification, de portabilité et de suppression de ses données personnelles rattachées à son identifiant unique.
								</div>
							</CardBody>
						</Card>
					</div>

					<div className="stack">
						{/* Card Zone Sensible & RGPD */}
						<Card style={{ border: "1px solid var(--a-danger-soft, #f8d9d2)", overflow: "hidden" }}>
							{/* Top Accent Danger Bar */}
							<div style={{ height: 4, background: "var(--a-danger, #b23a2a)" }} />
							<CardHead
								title="Zone Sensible & RGPD"
								sub="Actions critiques et gestion de l'état du compte client"
								right={
									<Tag variant="danger" noDot>
										<span style={{ marginRight: 3, display: "inline-flex", alignItems: "center" }}>
											<AdminIcon n="alert" s={11} />
										</span>
										Zone critique
									</Tag>
								}
							/>
							<CardBody style={{ display: "grid", gap: 16 }}>
								{/* Account Suspension Box */}
								<div
									style={{
										padding: 14,
										background: c.status === "active" ? "var(--a-surface-2, #f2eee3)" : "var(--a-danger-soft, #f8d9d2)",
										border: "1px solid var(--a-border)",
										borderRadius: 10,
										display: "grid",
										gap: 10,
									}}
								>
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<strong style={{ fontSize: 13, color: "var(--a-ink)" }}>Suspension du compte client</strong>
										<Tag variant={c.status === "active" ? "success" : "danger"}>
											{c.status === "active" ? "Compte Actif" : "Compte Suspendu"}
										</Tag>
									</div>

									<p className="muted" style={{ fontSize: 12, margin: 0, lineHeight: 1.4 }}>
										Empêche le client de passer de nouvelles commandes ou d'accéder à son espace personnel. L'historique d'achats est conservé.
									</p>

									{c.status === "active" ? (
										<AdminButton variant="secondary" onClick={() => setShowSuspendModal(true)} style={{ justifySelf: "start" }}>
											<AdminIcon n="lock" s={14} /> Suspendre le compte
										</AdminButton>
									) : (
										<AdminButton variant="primary" onClick={handleReactivate} style={{ justifySelf: "start" }}>
											<AdminIcon n="check" s={14} /> Réactiver le compte
										</AdminButton>
									)}
								</div>

								<hr style={{ border: "none", borderTop: "1px solid var(--a-border)", margin: 0 }} />

								{/* Irreversible Anonymization (GDPR) */}
								<div
									style={{
										padding: 14,
										background: "var(--a-danger-soft, #f8d9d2)",
										border: "1px solid rgba(178, 58, 42, 0.3)",
										borderRadius: 10,
										display: "grid",
										gap: 12,
									}}
								>
									<div>
										<strong style={{ color: "var(--a-danger, #B23A2A)", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
											<AdminIcon n="trash" s={16} c="var(--a-danger)" />
											Anonymisation irréversible (RGPD)
										</strong>
										<p className="muted" style={{ fontSize: 12, marginTop: 4, marginBottom: 0, lineHeight: 1.45 }}>
											Efface définitivement les données personnelles du client (Droit à l'oubli / Article 17 RGPD).
										</p>
									</div>

									<div style={{ fontSize: 11, color: "var(--a-danger, #B23A2A)", display: "grid", gap: 4 }}>
										<div>• ❌ Suppression irréversible du nom, email, téléphone et adresses.</div>
										<div>• 🔒 Conservation anonymisée de l'historique financier à des fins comptables.</div>
										<div>• ⚠️ Action irréversible consignée dans le journal d'audit de sécurité.</div>
									</div>

									<AdminButton
										variant="secondary"
										className="text-red-600"
										onClick={() => {
											setAnonymizeReason("");
											setAnonymizeConfirmName("");
											setAnonymizeApprovalId("");
											setShowAnonymizeModal(true);
										}}
										style={{
											justifySelf: "start",
											borderColor: "var(--a-danger, #b23a2a)",
											color: "var(--a-danger, #b23a2a)",
											fontWeight: 700,
										}}
									>
										<AdminIcon n="trash" s={14} /> Anonymiser le client (RGPD)
									</AdminButton>
								</div>
							</CardBody>
						</Card>
					</div>
				</div>
			)}



			{/* MODALES */}

			{/* Modale motif PII */}
			{showPiiModal && (
				<Modal
					title="Motif d'accès aux données personnelles (PII)"
					subtitle="Votre rôle exige la saisie d'un motif légitime pour consulter les coordonnées complètes du client. Ce motif sera consigné dans le journal d'audit de sécurité."
					onClose={() => setShowPiiModal(false)}
					width={520}
				>
					<div style={{ display: "grid", gap: 16 }}>
						<div>
							<label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
								Saisir le motif d'accès *
							</label>
							<input
								className="inp"
								placeholder="Ex : Assistance téléphonique suite à réclamation livraison"
								value={piiReasonInput}
								onChange={(e) => setPiiReasonInput(e.target.value)}
								autoFocus
							/>
						</div>

						<div>
							<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-soft)", display: "block", marginBottom: 6 }}>
								Motifs fréquents (sélection rapide) :
							</span>
							<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
								{[
									"Assistance téléphonique réclamation",
									"Vérification adresse livraison",
									"Gestion de litige / remboursement",
									"Contrôle de sécurité / anti-fraude",
								].map((preset) => (
									<button
										key={preset}
										type="button"
										onClick={() => setPiiReasonInput(preset)}
										style={{
											padding: "5px 10px",
											borderRadius: 6,
											border: "1px solid var(--a-border)",
											background: piiReasonInput === preset ? "var(--a-primary-soft, #fdf6f0)" : "var(--a-surface)",
											color: piiReasonInput === preset ? "var(--a-primary, #c6563a)" : "var(--a-ink)",
											fontSize: 12,
											fontWeight: piiReasonInput === preset ? 600 : 400,
											cursor: "pointer",
											transition: "all 0.15s ease",
										}}
									>
										+ {preset}
									</button>
								))}
							</div>
						</div>

						<ModalFooter>
							<AdminButton variant="ghost" onClick={() => setShowPiiModal(false)}>
								Annuler
							</AdminButton>
							<AdminButton variant="primary" onClick={handleConfirmPiiReason} disabled={!piiReasonInput.trim()}>
								<AdminIcon n="shield" s={14} /> Valider et consulter
							</AdminButton>
						</ModalFooter>
					</div>
				</Modal>
			)}

			{/* Modale journal PII */}
			{piiLogs && (
				<Modal
					title="Journal d'accès PII"
					subtitle={`Historique des consultations des données sensibles (${piiLogs.length} au total)`}
					onClose={() => {
						setPiiLogs(null);
						setPiiLogSearch("");
					}}
					width={560}
				>
					<div style={{ display: "grid", gap: 12 }}>
						{piiLogs.length > 0 && (
							<div style={{ position: "relative" }}>
								<input
									className="inp"
									placeholder="Filtrer par nom d'agent, date ou motif..."
									value={piiLogSearch}
									onChange={(e) => setPiiLogSearch(e.target.value)}
									style={{ fontSize: 13, paddingLeft: 32 }}
								/>
								<div
									style={{
										position: "absolute",
										left: 10,
										top: "50%",
										transform: "translateY(-50%)",
										opacity: 0.45,
										display: "flex",
										pointerEvents: "none",
									}}
								>
									<AdminIcon n="search" s={14} />
								</div>
								{piiLogSearch && (
									<button
										type="button"
										onClick={() => setPiiLogSearch("")}
										style={{
											position: "absolute",
											right: 10,
											top: "50%",
											transform: "translateY(-50%)",
											background: "none",
											border: "none",
											opacity: 0.5,
											cursor: "pointer",
											padding: 2,
										}}
									>
										<AdminIcon n="close" s={12} />
									</button>
								)}
							</div>
						)}

						<div style={{ maxHeight: 380, overflowY: "auto", display: "grid", gap: 8, paddingRight: 2 }}>
							{filteredPiiLogs.length === 0 ? (
								<div
									style={{
										padding: "32px 16px",
										textAlign: "center",
										background: "var(--a-app-bg, #f9f6ef)",
										borderRadius: 10,
										border: "1px dashed var(--a-border)",
									}}
								>
									<span style={{ opacity: 0.5, marginBottom: 8, display: "inline-block" }}>
										<AdminIcon n="shield" s={24} />
									</span>
									<div style={{ fontWeight: 600, fontSize: 14 }}>
										{piiLogs.length === 0 ? "Aucun accès PII enregistré" : "Aucun résultat pour cette recherche"}
									</div>
									<div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
										{piiLogs.length === 0
											? "Toutes les consultations de coordonnées par les agents sont consignées ici."
											: "Essayez de modifier votre mot-clé de recherche."}
									</div>
								</div>
							) : (
								filteredPiiLogs.map((log) => {
									const dateFormatted = formatLogDate(log);
									return (
										<div
											key={log.id}
											style={{
												padding: "10px 12px",
												background: "var(--a-surface)",
												borderRadius: 8,
												border: "1px solid var(--a-border)",
												display: "grid",
												gap: 6,
												transition: "background 0.15s ease",
											}}
										>
											<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
												<div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 13, color: "var(--a-ink)" }}>
													<div
														style={{
															width: 26,
															height: 26,
															borderRadius: "50%",
															background: "var(--a-primary-soft, rgba(198,86,58,0.1))",
															color: "var(--a-primary, #c6563a)",
															display: "grid",
															placeItems: "center",
															flexShrink: 0,
														}}
													>
														<AdminIcon n="user" s={13} />
													</div>
													<span>{log.actorLabel}</span>
												</div>
												<span className="muted mono" style={{ fontSize: 11, flexShrink: 0 }}>
													{dateFormatted}
												</span>
											</div>

											{log.reason ? (
												<div style={{ fontSize: 12.5, color: "var(--a-ink-soft)", paddingLeft: 34, lineHeight: 1.4 }}>
													<span style={{ fontWeight: 600, color: "var(--a-ink)", fontSize: 11, marginRight: 4 }}>Motif :</span>
													<span>{log.reason}</span>
												</div>
											) : (
												<div style={{ fontSize: 11.5, color: "var(--a-ink-soft)", paddingLeft: 34, fontStyle: "italic" }}>
													Motif non spécifié
												</div>
											)}
										</div>
									);
								})
							)}
						</div>

						<ModalFooter>
							<AdminButton
								variant="ghost"
								onClick={() => {
									setPiiLogs(null);
									setPiiLogSearch("");
								}}
							>
								Fermer
							</AdminButton>
						</ModalFooter>
					</div>
				</Modal>
			)}

			{/* Modale Édition Profil */}
			{showEditModal && (
				<Modal
					title="Modifier la fiche client"
					subtitle="Mettre à jour les coordonnées et informations principales du compte client."
					onClose={() => setShowEditModal(false)}
					width={560}
				>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							handleUpdateProfile();
						}}
						style={{ display: "grid", gap: 16 }}
					>
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600 }}>Nom complet *</span>
								<input
									className="inp"
									value={editForm.name}
									onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
									placeholder="Nom complet du client"
									required
								/>
							</label>

							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600 }}>Téléphone *</span>
								<input
									className="inp mono"
									value={editForm.phone}
									onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
									placeholder="+225 0700000000"
									required
								/>
							</label>
						</div>

						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600 }}>Adresse email</span>
								<input
									className="inp mono"
									type="email"
									value={editForm.email}
									onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
									placeholder="client@exemple.com"
								/>
							</label>

							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600 }}>Ville / Commune</span>
								<input
									className="inp"
									value={editForm.city}
									onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
									placeholder="Ex : Abidjan (Cocody)"
								/>
							</label>
						</div>

						<ModalFooter>
							<AdminButton type="button" variant="ghost" onClick={() => setShowEditModal(false)}>
								Annuler
							</AdminButton>
							<AdminButton type="submit" variant="primary" disabled={!editForm.name.trim() || !editForm.phone.trim()}>
								<AdminIcon n="check" s={14} /> Enregistrer les modifications
							</AdminButton>
						</ModalFooter>
					</form>
				</Modal>
			)}

			{/* Modale Suspension */}
			{showSuspendModal && (
				<Modal
					title="Suspendre le compte client"
					subtitle="Empêcher temporairement le client de passer des commandes ou de se connecter."
					onClose={() => setShowSuspendModal(false)}
					width={520}
				>
					<div style={{ display: "grid", gap: 16 }}>
						{/* Impact Alert */}
						<div
							style={{
								padding: "12px 14px",
								background: "#fffbeb",
								border: "1px solid #fde68a",
								borderRadius: 8,
								display: "flex",
								gap: 10,
								fontSize: 12.5,
								color: "#92400e",
							}}
						>
							<span style={{ color: "#d97706", flexShrink: 0, marginTop: 1, display: "inline-flex" }}>
								<AdminIcon n="alert" s={18} c="#d97706" />
							</span>
							<div>
								<strong>Conséquences de la suspension :</strong>
								<ul style={{ margin: "4px 0 0 16px", padding: 0, lineHeight: 1.4 }}>
									<li>Accès à l'espace client bloqué.</li>
									<li>Impossible de valider de nouvelles commandes.</li>
									<li>Historique et points de fidélité conservés.</li>
								</ul>
							</div>
						</div>

						<div>
							<label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
								Motif obligatoire de suspension *
							</label>
							<input
								className="inp"
								placeholder="Ex : Suspicions de fraude sur paiement"
								value={suspendReason}
								onChange={(e) => setSuspendReason(e.target.value)}
								autoFocus
							/>
						</div>

						<div>
							<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-soft)", display: "block", marginBottom: 6 }}>
								Motifs fréquents (sélection rapide) :
							</span>
							<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
								{[
									"Suspicions de fraude paiement",
									"Non-respect des CGU",
									"Demande expresse du client",
									"Litige livraison / impayé",
								].map((preset) => (
									<button
										key={preset}
										type="button"
										onClick={() => setSuspendReason(preset)}
										style={{
											padding: "5px 10px",
											borderRadius: 6,
											border: "1px solid var(--a-border)",
											background: suspendReason === preset ? "var(--a-primary-soft, #fdf6f0)" : "var(--a-surface)",
											color: suspendReason === preset ? "var(--a-primary, #c6563a)" : "var(--a-ink)",
											fontSize: 12,
											fontWeight: suspendReason === preset ? 600 : 400,
											cursor: "pointer",
											transition: "all 0.15s ease",
										}}
									>
										+ {preset}
									</button>
								))}
							</div>
						</div>

						<ModalFooter>
							<AdminButton variant="ghost" onClick={() => setShowSuspendModal(false)}>
								Annuler
							</AdminButton>
							<AdminButton variant="secondary" className="text-red-600" onClick={handleSuspend} disabled={!suspendReason.trim()}>
								<AdminIcon n="lock" s={14} /> Confirmer la suspension
							</AdminButton>
						</ModalFooter>
					</div>
				</Modal>
			)}

			{/* Modale Anonymisation */}
			{showAnonymizeModal && (
				<Modal
					title="Anonymisation irréversible (RGPD)"
					subtitle="Effacement définitif de toutes les données personnelles identifiables (Droit à l'oubli Art. 17)."
					onClose={() => setShowAnonymizeModal(false)}
					width={560}
				>
					<div style={{ display: "grid", gap: 16 }}>
						{/* Danger Alert Box */}
						<div
							style={{
								padding: "12px 14px",
								background: "#fef2f2",
								border: "1px solid #fecaca",
								borderRadius: 8,
								display: "flex",
								gap: 10,
								fontSize: 12.5,
								color: "#991b1b",
							}}
						>
							<span style={{ color: "#dc2626", flexShrink: 0, marginTop: 1, display: "inline-flex" }}>
								<AdminIcon n="trash" s={18} c="#dc2626" />
							</span>
							<div>
								<strong>Action irréversible :</strong>
								<ul style={{ margin: "4px 0 0 16px", padding: 0, lineHeight: 1.4 }}>
									<li>Nom, email, téléphone et adresses seront supprimés définitivement.</li>
									<li>Le solde de fidélité et parrainage sera remis à zéro.</li>
									<li>Seules les données statistiques anonymes d'achats seront conservées pour la comptabilité.</li>
								</ul>
							</div>
						</div>

						<div style={{ display: "grid", gap: 12 }}>
							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600 }}>Motif RGPD / légal *</span>
								<input
									className="inp"
									placeholder="Ex : Demande d'effacement droit à l'oubli"
									value={anonymizeReason}
									onChange={(e) => setAnonymizeReason(e.target.value)}
								/>
							</label>

							<div>
								<span style={{ fontSize: 11, fontWeight: 600, color: "var(--a-ink-soft)", display: "block", marginBottom: 6 }}>
									Motifs légaux courants :
								</span>
								<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
									{[
										"Droit à l'oubli (Art. 17 RGPD)",
										"Fin de conservation légale",
										"Demande explicite du client",
										"Ordonnance / Décision légale",
									].map((preset) => (
										<button
											key={preset}
											type="button"
											onClick={() => setAnonymizeReason(preset)}
											style={{
												padding: "4px 10px",
												borderRadius: 6,
												border: "1px solid var(--a-border)",
												background: anonymizeReason === preset ? "var(--a-primary-soft, #fdf6f0)" : "var(--a-surface)",
												color: anonymizeReason === preset ? "var(--a-primary, #c6563a)" : "var(--a-ink)",
												fontSize: 11,
												cursor: "pointer",
											}}
										>
											+ {preset}
										</button>
									))}
								</div>
							</div>

							<label style={{ display: "grid", gap: 4 }}>
								<span style={{ fontSize: 12, fontWeight: 600 }}>ID d'approbation 4-eyes (optionnel)</span>
								<input
									className="inp mono"
									placeholder="ID validation manager si requis"
									value={anonymizeApprovalId}
									onChange={(e) => setAnonymizeApprovalId(e.target.value)}
								/>
							</label>

							<label style={{ display: "grid", gap: 4 }}>
								<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
									<span style={{ fontSize: 12, fontWeight: 600 }}>
										Confirmation : tapez <strong style={{ color: "var(--a-ink)" }}>{c.name}</strong> *
									</span>
									{anonymizeConfirmName.trim() && (
										<span
											style={{
												fontSize: 11,
												fontWeight: 700,
												color: anonymizeConfirmName.trim() === c.name.trim() ? "#16a34a" : "#dc2626",
											}}
										>
											{anonymizeConfirmName.trim() === c.name.trim() ? "✓ Correspondance exacte" : "❌ Nom incorrect"}
										</span>
									)}
								</div>
								<input
									className="inp"
									placeholder={c.name}
									value={anonymizeConfirmName}
									onChange={(e) => setAnonymizeConfirmName(e.target.value)}
								/>
							</label>

							<label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", marginTop: 4 }}>
								<input
									type="checkbox"
									checked={anonymizeConsent}
									onChange={(e) => setAnonymizeConsent(e.target.checked)}
									style={{ marginTop: 2 }}
								/>
								<span style={{ fontSize: 12, color: "var(--a-ink)", lineHeight: 1.4 }}>
									Je confirme avoir vérifié l'identité du demandeur et comprends que cette suppression est <strong>définitive et irréversible</strong>.
								</span>
							</label>
						</div>

						<ModalFooter>
							<AdminButton variant="ghost" onClick={() => setShowAnonymizeModal(false)}>
								Annuler
							</AdminButton>
							<AdminButton
								variant="secondary"
								className="text-red-600"
								disabled={
									anonymizeConfirmName.trim() !== c.name.trim() ||
									!anonymizeReason.trim() ||
									!anonymizeConsent
								}
								onClick={handleAnonymize}
							>
								<AdminIcon n="trash" s={14} /> Anonymiser définitivement
							</AdminButton>
						</ModalFooter>
					</div>
				</Modal>
			)}
		</>
	);
}

function Row({
	label,
	value,
	mono,
}: {
	label: string;
	value: string;
	mono?: boolean;
}) {
	return (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				padding: "6px 0",
				gap: 12,
			}}
		>
			<span className="muted">{label}</span>
			<strong
				className={mono ? "mono" : undefined}
				style={{ textAlign: "right" }}
			>
				{value}
			</strong>
		</div>
	);
}

function CopyIcon({ s = 13, c = "currentColor" }: { s?: number; c?: string }) {
	return (
		<svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
			<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
		</svg>
	);
}

function RowWithAction({
	label,
	value,
	mono,
	onCopy,
}: {
	label: string;
	value: string;
	mono?: boolean;
	onCopy?: () => void;
}) {
	return (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				padding: "6px 0",
				gap: 12,
			}}
		>
			<span className="muted">{label}</span>
			<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
				<strong className={mono ? "mono" : undefined} style={{ textAlign: "right" }}>
					{value}
				</strong>
				{onCopy && value && value !== "—" && (
					<button
						type="button"
						onClick={onCopy}
						style={{
							background: "var(--a-app-bg, #f9f6ef)",
							border: "1px solid var(--a-border)",
							cursor: "pointer",
							color: "var(--a-ink-secondary)",
							padding: "3px 6px",
							borderRadius: 4,
							display: "inline-flex",
							alignItems: "center",
							gap: 4,
							fontSize: 11,
							transition: "all 0.15s ease",
						}}
						title="Copier dans le presse-papier"
					>
						<CopyIcon s={12} />
					</button>
				)}
			</div>
		</div>
	);
}
